/**
 * Which edition of a demo plays (docs/plans/phone-edition.md): the desktop steps, or the phone
 * edition (`DemoDoc.phone`) written for a phone. Pure and zod-free: the player, Studio's preview
 * and the video exporter all use it, so they agree on one demo.
 *
 * The choice is made once, when the player mounts. A forced choice (the `edition` attribute, or
 * `?edition=phone|desktop` on a link) wins; otherwise a portrait box narrower than a tablet gets
 * the phone edition. A document without one plays its desktop steps everywhere, as before.
 */
import type { DemoDoc } from './demo';
import { STORY_MAX_WIDTH_PX, STORY_PORTRAIT } from './camera';

export type Edition = 'desktop' | 'phone';

/** `'phone'` / `'desktop'` from an attribute or query value; anything else is no choice. */
export function parseEdition(raw: string | null | undefined): Edition | null {
  const v = raw?.trim().toLowerCase();
  return v === 'phone' || v === 'desktop' ? v : null;
}

/** The edition a viewer of this box gets. */
export function pickEdition(
  doc: Pick<DemoDoc, 'phone'>,
  box: { w: number; h: number },
  forced?: Edition | null,
): Edition {
  if (!doc.phone) return 'desktop';
  if (forced) return forced;
  return box.w > 0 && box.w < STORY_MAX_WIDTH_PX && box.h >= box.w * STORY_PORTRAIT
    ? 'phone'
    : 'desktop';
}

/**
 * The document as the chosen edition plays it: for the phone edition, its steps, start,
 * chapters and end card in place of the desktop ones. Same object when nothing changes.
 */
export function applyEdition<D extends DemoDoc>(doc: D, edition: Edition): D {
  const p = doc.phone;
  if (edition !== 'phone' || !p) return doc;
  return {
    ...doc,
    start: p.start,
    steps: p.steps,
    chapters: p.chapters ?? [],
    // The document's own end card, unless it names a desktop step to follow.
    cta:
      p.cta !== undefined
        ? p.cta
        : doc.cta &&
            doc.cta.afterStep !== 'last' &&
            !p.steps.some((s) => s.id === doc.cta!.afterStep)
          ? { ...doc.cta, afterStep: 'last' }
          : doc.cta,
    gate: phoneGate(doc.gate, p.steps),
  };
}

/**
 * A gate names a desktop step; the phone edition tells that step in one or more steps of its
 * own (`of`). The form comes after the last of them, so a phone viewer meets it at the same
 * point in the story. A gate on a phone step id stays; one whose step the phone edition does
 * not tell at all is dropped (there is no honest place to put it).
 */
export function phoneGate(
  gate: DemoDoc['gate'],
  steps: ReadonlyArray<{ id: string; of?: string }>,
): DemoDoc['gate'] {
  if (!gate) return null;
  if (steps.some((s) => s.id === gate.afterStep)) return gate;
  let last: string | null = null;
  for (const s of steps) if (s.of === gate.afterStep) last = s.id;
  return last ? { ...gate, afterStep: last } : null;
}

/**
 * A deep link (`?s=`) into the other edition: a desktop step id opens the first phone step that
 * tells it (`of`), and a phone step id opens the desktop step it tells. Unknown ids pass through
 * (the player falls back to the start).
 */
export function stepInEdition(
  doc: Pick<DemoDoc, 'phone' | 'steps'>,
  stepId: string | null,
  edition: Edition,
): string | null {
  const p = doc.phone;
  if (!stepId || !p) return stepId;
  if (edition === 'phone') {
    if (p.steps.some((s) => s.id === stepId)) return stepId;
    return p.steps.find((s) => s.of === stepId)?.id ?? stepId;
  }
  if (doc.steps.some((s) => s.id === stepId)) return stepId;
  return p.steps.find((s) => s.id === stepId)?.of ?? stepId;
}

/**
 * Run a whole-document transform over both editions: the desktop steps first, then the phone
 * edition's steps seen as a document of their own (sharing the assets the first pass left), and
 * fold the phone result back into `phone`. For publish, voice, translation and export code that
 * walks `doc.steps`: written once, it covers the phone edition too. Assets and audio assets come
 * from the last pass, so a pass that adds or rewrites them sees the other's changes.
 */
export function mapEditions<D extends DemoDoc>(doc: D, f: (d: D, edition: Edition) => D): D {
  const desk = f(doc, 'desktop');
  if (!doc.phone) return desk;
  return foldPhone(desk, doc.phone, f(phoneView(desk, doc.phone), 'phone'));
}

/** `mapEditions` for a transform that is async (it downloads a frame, calls a model). */
export async function mapEditionsAsync<D extends DemoDoc>(
  doc: D,
  f: (d: D, edition: Edition) => Promise<D>,
): Promise<D> {
  const desk = await f(doc, 'desktop');
  if (!doc.phone) return desk;
  return foldPhone(desk, doc.phone, await f(phoneView(desk, doc.phone), 'phone'));
}

/** The phone edition seen as a document of its own, on the assets the desktop pass left. */
function phoneView<D extends DemoDoc>(desk: D, p: NonNullable<DemoDoc['phone']>): D {
  const seen = applyEdition({ ...desk, phone: p }, 'phone');
  return { ...seen, phone: undefined };
}

function foldPhone<D extends DemoDoc>(desk: D, p: NonNullable<DemoDoc['phone']>, ph: D): D {
  return {
    ...desk,
    assets: ph.assets,
    audioAssets: ph.audioAssets,
    phone: {
      ...p,
      start: ph.start,
      // A transform sees the phone steps as plain `Step`s; if it re-parses them, zod strips the
      // phone-only `of`. Put it back from the steps it came from, matched by id.
      steps: ph.steps.map((s) => {
        const of = p.steps.find((o) => o.id === s.id)?.of;
        return of !== undefined && (s as { of?: string }).of === undefined ? { ...s, of } : s;
      }),
      chapters: ph.chapters,
      ...(p.cta === undefined ? {} : { cta: ph.cta }),
    },
  };
}

/** Every step of both editions, desktop first: for read-only walks (references, checks). */
export function allSteps(doc: Pick<DemoDoc, 'steps' | 'phone'>): DemoDoc['steps'] {
  return doc.phone ? [...doc.steps, ...doc.phone.steps] : doc.steps;
}
